import { useEffect, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import type { TenantFeatures } from '../../lib/superAdmin';
import { isFeatureEnabled } from '../../lib/superAdmin';
import { PLANS, MODULE_OPTIONS, planContents, type ModuleKey, type PlanKey, type PlanDef } from '../../lib/plans';
import { requestOption, requestPlan } from '../../lib/optionRequestService';
import { loadTenantPendingKeys } from '../../lib/optionRequestsService';

/**
 * Onglet unique « Options & Services » (remplace les onglets éparpillés Fidélité /
 * Avis / Campagnes / YumLink Contrôle). Regroupe :
 *  - les 3 FORMULES (Gratuit / Medium / Premium) avec ce que chacune contient,
 *  - les OPTIONS à la carte (statut actif/inactif, prix solo, bénéfices).
 * Freemium : une option est ACTIVE seulement si explicitement accordée (isFeatureEnabled).
 * Le client peut « Gérer » une option active (→ sa section) ou « Demander » une option /
 * une formule inactive (→ email au super admin via `request-option`). L'activation reste
 * un geste du super admin.
 */

/** Option → id de section admin (pour « Gérer »). */
const MODULE_SECTION: Record<ModuleKey, string> = {
  loyalty: 'loyalty',
  reviews: 'nps',
  campaigns: 'campaigns',
  yumlink_controle: 'yumlink_controle',
};

type ReqState = 'idle' | 'sending' | 'done' | 'err';

export default function OptionsServicesHub({
  tenant,
  features = {},
  onOpen,
}: {
  tenant: Tenant;
  features?: TenantFeatures;
  onOpen: (sectionId: string) => void;
}) {
  const [state, setState] = useState<Record<string, ReqState>>({});
  const [msg, setMsg] = useState<string | null>(null);
  // Clés (option ou formule) déjà DEMANDÉES et en attente → badge « demande en cours ».
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    loadTenantPendingKeys(tenant.id).then(setPendingKeys);
  }, [tenant.id]);
  const markPending = (key: string) => setPendingKeys((s) => new Set(s).add(key));

  const isActive = (k: ModuleKey) => isFeatureEnabled(features, k);
  // La colonne `plan` du tenant n'est pas typée dans Tenant → accès sûr (pas de mise en avant si absente).
  const tenantPlan = (tenant as unknown as { plan?: string | null }).plan ?? undefined;
  const currentPlan: PlanKey | null = PLANS.find((p) => p.key === tenantPlan)?.key ?? null;

  const setReq = (id: string, s: ReqState) => setState((m) => ({ ...m, [id]: s }));

  const askOption = async (opt: (typeof MODULE_OPTIONS)[number]) => {
    setReq(opt.key, 'sending');
    setMsg(null);
    const r = await requestOption({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantSlug: tenant.slug,
      moduleKey: opt.key,
      moduleTitle: opt.title,
      tenantEmail: tenant.email,
    });
    setReq(opt.key, r.ok ? 'done' : 'err');
    if (r.ok) markPending(opt.key);
    setMsg(
      r.ok
        ? `✅ Demande envoyée pour « ${opt.title} ». Lil & Co la traite et vous confirmera l'activation.`
        : `⚠️ ${r.error ?? 'Demande impossible.'}`,
    );
  };

  const askPlan = async (p: PlanDef) => {
    const id = `plan:${p.key}`;
    setReq(id, 'sending');
    setMsg(null);
    const r = await requestPlan({ tenantId: tenant.id, tenantName: tenant.name, tenantSlug: tenant.slug, planKey: p.key, planLabel: p.label, tenantEmail: tenant.email });
    setReq(id, r.ok ? 'done' : 'err');
    if (r.ok) markPending(p.key);
    setMsg(
      r.ok
        ? `✅ Demande envoyée pour la formule « ${p.label} ». Lil & Co la traite et vous confirmera l'activation.`
        : `⚠️ ${r.error ?? 'Demande impossible.'}`,
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-bold">Options &amp; Services</h2>
        <p className="text-sm text-slate-500">
          Choisissez une <strong>formule</strong> complète ou activez une <strong>option à la carte</strong>. Votre demande
          nous est envoyée&nbsp;; on vous confirme l'activation par email et dans votre espace.
        </p>
      </div>

      {/* ---- Formules ---- */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Nos formules</div>
        <div className="grid gap-3 md:grid-cols-3">
          {PLANS.map((p) => {
            const isCurrent = currentPlan === p.key;
            const reqId = `plan:${p.key}`;
            const st = state[reqId] ?? 'idle';
            return (
              <div
                key={p.key}
                className={
                  'flex flex-col rounded-xl border p-4 ' +
                  (isCurrent ? 'border-emerald-400 bg-emerald-50' : p.highlight ? 'border-brand-primary bg-brand-light/40' : 'border-slate-200')
                }
              >
                <div className="flex items-center justify-between">
                  <div className="text-base font-bold">{p.label}</div>
                  {isCurrent ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Votre formule</span>
                  ) : p.highlight ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">★ populaire</span>
                  ) : null}
                </div>
                <div className="mt-1 text-xl font-extrabold">{p.priceLabel}</div>
                <p className="mt-1 text-xs text-slate-500">{p.tagline}</p>
                <ul className="mt-3 flex flex-col gap-1 border-t border-slate-100 pt-3 text-sm">
                  {planContents(p.key).map((line, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span>{line.emoji}</span>
                      <span>{line.label}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3">
                  {p.key === 'gratuit' ? (
                    // Socle gratuit : actif d'office pour tous → aucune demande à faire.
                    <div className="rounded-lg bg-slate-100 py-1.5 text-center text-xs font-semibold text-slate-500">
                      Socle gratuit — toujours inclus
                    </div>
                  ) : isCurrent ? (
                    <div className="rounded-lg bg-emerald-100 py-1.5 text-center text-xs font-semibold text-emerald-700">Formule active</div>
                  ) : pendingKeys.has(p.key) ? (
                    <div className="rounded-lg bg-amber-100 py-1.5 text-center text-xs font-semibold text-amber-700">🟡 Demande en cours</div>
                  ) : (
                    <button
                      type="button"
                      disabled={st === 'sending' || st === 'done'}
                      onClick={() => askPlan(p)}
                      className="w-full rounded-lg py-2 text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: 'var(--brand-primary)' }}
                    >
                      {st === 'sending' ? 'Envoi…' : st === 'done' ? '✅ Demande envoyée' : 'Demander cette formule'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- Options à la carte ---- */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Options à la carte</div>
        <div className="grid gap-3 md:grid-cols-2">
          {MODULE_OPTIONS.map((opt) => {
            const active = isActive(opt.key);
            const st = state[opt.key] ?? 'idle';
            const inPlans = PLANS.filter((p) => opt.includedIn.includes(p.key)).map((p) => p.label);
            return (
              <div key={opt.key} className="flex flex-col rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{opt.emoji}</span>
                    <span className="font-bold">{opt.title}</span>
                  </div>
                  {active ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">✅ Active</span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">🔒 Inactive</span>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">{opt.tagline}</p>
                <ul className="mt-2 flex flex-col gap-0.5 text-xs text-slate-600">
                  {opt.benefits.slice(0, 3).map((b, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-emerald-500">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-slate-400">
                  {opt.soloPriceLabel} à la carte{inPlans.length > 0 ? ` · incluse dans ${inPlans.join(' & ')}` : ''}
                </div>
                <div className="mt-3">
                  {active ? (
                    <button
                      type="button"
                      onClick={() => onOpen(MODULE_SECTION[opt.key])}
                      className="w-full rounded-lg border border-slate-300 py-2 text-sm font-semibold hover:bg-slate-50"
                    >
                      ⚙️ Gérer
                    </button>
                  ) : pendingKeys.has(opt.key) ? (
                    <div className="rounded-lg bg-amber-100 py-2 text-center text-sm font-semibold text-amber-700">🟡 Demande en cours</div>
                  ) : (
                    <button
                      type="button"
                      disabled={st === 'sending' || st === 'done'}
                      onClick={() => askOption(opt)}
                      className="w-full rounded-lg py-2 text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: 'var(--brand-primary)' }}
                    >
                      {st === 'sending' ? 'Envoi…' : st === 'done' ? '✅ Demande envoyée' : "Demander l'activation"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {msg && <p className="text-sm">{msg}</p>}
    </div>
  );
}
